import React, { useCallback, useEffect, useRef, useState } from "react";

import { AlertCircle, ArrowRight, RefreshCw } from "lucide-react";
import { useParams } from "react-router-dom";
import useBreakpoint from "use-breakpoint";

import { getFollowUpQuestionByReminder, getReminderDetail } from "@/apis";
import { MarkdownContent } from "@/components/Typewriter";
import { BREAKPOINTS } from "@/constants/defaultValue";
import { getSMBaseUrl } from "@/lib/utils";
import useGlobalStore from "@/stores/useGlobalStore";


import "../style.css";
import FollowUpQuestion from "./FollowUpQuestion";

const Reminder = () => {
  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);

  const { reminderId } = useParams();
  const business = useGlobalStore((state) => state.business);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { breakpoint } = useBreakpoint(BREAKPOINTS);
  const [reminder, setReminder] = useState(null);

  const isMobile = ["xs", "sm"].includes(breakpoint);

  const fetchReminder = async () => {
    if (!business) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getReminderDetail({ business, reminderId });
      console.log("Reminder response:", response);

      setReminder(response);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch reminders:", err);
      setError("Không thể tải dữ liệu. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReminder();
  }, [business]);

  const handleRefresh = () => {
    fetchReminder();
  };

  const handleActionClick = (action) => {
    if (action.action === "VIEW") {
      if (isMobile) {
        window.open(`kidosm://open?p=${action.viewPath}`, "_system");
        return;
      }
      const SMBaseUrl = getSMBaseUrl();
      window.open(`${SMBaseUrl}/${action.viewPath}`, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500 mr-2" />
          <span className="text-gray-600">Đang tải dữ liệu...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <AlertCircle className="w-8 h-8 text-red-500" />
          <p className="text-red-600 text-center">{error}</p>
          <button onClick={handleRefresh} className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
            <RefreshCw className="w-4 h-4" />
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[820px] mx-auto p-4 pl-6" ref={containerRef}>
      <div
        className="p-4 shadow-sm relative bg-[#208DF10D] rounded-lg transition-all duration-200 hover:shadow-md group"
      >
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <span className={`text-xs text-gray-400`}>{reminder.time}</span>
        </div>

        <div className="flex items-start gap-3">
          <div className="flex-1 space-y-6 w-full overflow-x-scroll">
            <h3 className={`font-medium text-gray-900 leading-relaxed pr-12 ${isMobile ? "text-base" : "text-xl"}`}>{reminder.title}</h3>

            <MarkdownContent content={reminder.content} className="text-gray-700 leading-relaxed text-sm reminder-markdown" />

            {reminder.actions?.length > 0 && (
              <div className="flex items-center gap-2">
                {reminder.actions.map((action, index) => (
                  <button
                    key={action.id || index}
                    className="flex items-center gap-4 justify-between cursor-pointer px-4 py-2 bg-gradient-to-r bg-white text-sm  hover:bg-slate-50  rounded-lg transition-colors"
                    onClick={() => handleActionClick(action)}
                  >
                    {action.label}

                    <span className="text-xs text-gray-400">
                      <ArrowRight size={20} strokeWidth={1.5} />{" "}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <FollowUpQuestion isPopupContent={isMobile} />
      <div ref={messagesEndRef} />
    </div>
  );
};

export default React.memo(Reminder);
